import React, { useState, useEffect } from "react";
import {
  Phone,
  Users,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Clock,
  MoreVertical,
  Edit3,
  Trash2,
  User,
  Send,
  FileWarning,
} from "lucide-react";
import { attendanceStatusLabel } from "./attendanceStatus";
import { Dropdown } from "antd";
import {
  getAttendanceHistory,
  updateAttendanceRemark,
} from "../../../api/elearning/attendance/attendance";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import AttendanceHistoryFilter from "./AttendanceHistoryFilter";
import { todayLocalISODate } from "../../../utils/formatUtils";

const dropdownItems = [
  { key: "edit", label: "Chỉnh sửa ghi chú", icon: <Edit3 size={14} /> },
  { key: "detail", label: "Hồ sơ học viên", icon: <User size={14} /> },
  { key: "send_sms", label: "Gửi SMS thông báo", icon: <Send size={14} /> },
  { type: "divider" },
  { key: "delete", label: "Xóa bản ghi", icon: <Trash2 size={14} />, danger: true },
];

const AttendanceHistory = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => todayLocalISODate());
  const [attendanceData, setAttendanceData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedKeyword(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await getAttendanceHistory({
          date: selectedDate || undefined,
          keyword: debouncedKeyword || undefined,
          page,
          size: 20,
        });
        setAttendanceData(res.records || []);
        setTotal(res.total ?? 0);
      } catch {
        showErrorToast("Không thể tải lịch sử điểm danh");
        setAttendanceData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [selectedDate, page, debouncedKeyword, refreshKey]);

  const filteredData = attendanceData.filter(
    (item) =>
      !searchTerm ||
      (item.student_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.class_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.parent_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(item.parent_phone || "").includes(searchTerm)
  );

  const handleRemarkBlur = async (attendanceId, value, previousRemark) => {
    if (!attendanceId) return;
    const next = String(value ?? "").trim();
    const prev = String(previousRemark ?? "").trim();
    if (next === prev) return;

    try {
      await updateAttendanceRemark(attendanceId, next);
      showSuccessToast("Đã cập nhật ghi chú");
      setAttendanceData((prevRows) =>
        prevRows.map((r) =>
          r.attendance_id === attendanceId ? { ...r, staff_remark: next } : r
        )
      );
    } catch {
      showErrorToast("Không thể cập nhật ghi chú");
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      Present: {
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: CheckCircle2,
        text: "Có mặt",
      },
      Absent: {
        color: "bg-red-50 text-red-600 border-red-200",
        icon: XCircle,
        text: "Vắng KP",
      },
      AbsentUnexcused: {
        color: "bg-red-50 text-red-600 border-red-200",
        icon: XCircle,
        text: "Vắng KP",
      },
      AbsentExcused: {
        color: "bg-sky-50 text-sky-700 border-sky-200",
        icon: FileWarning,
        text: "Vắng CP",
      },
      Late: {
        color: "bg-orange-50 text-orange-700 border-orange-200",
        icon: Clock,
        text: "Đi muộn",
      },
    };
    const config = configs[status] || {
      color: "bg-gray-50 text-gray-600 border-gray-200",
      icon: Clock,
      text: attendanceStatusLabel(status),
    };
    const Icon = config.icon;

    return (
      <span
        className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border inline-flex items-center gap-1 w-fit ${config.color}`}
      >
        <Icon size={11} /> {config.text}
      </span>
    );
  };

  const RenderMobileCard = ({ item }) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3.5 space-y-3">
      <div className="flex justify-between items-start gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
            {(item.student_name || "?").charAt(0)}
          </div>
          <div className="min-w-0">
            <h4 className="font-bold text-gray-900 text-[15px] leading-snug truncate">
              {item.student_name}
            </h4>
            <p className="text-[11px] text-gray-400 font-medium truncate mt-0.5">
              {item.class_name}
            </p>
            {item.parent_name ? (
              <p className="text-[11px] text-gray-500 font-medium truncate mt-0.5 flex items-center gap-1">
                <Users size={10} /> {item.parent_name}
              </p>
            ) : null}
          </div>
        </div>
        {getStatusBadge(item.status)}
      </div>

      <div className="bg-slate-50 rounded-xl p-2.5 space-y-2">
        <div className="flex items-start gap-2">
          <MessageSquare size={12} className="text-gray-400 mt-0.5 shrink-0" />
          <p className="text-[12px] text-gray-600 leading-relaxed">
            <span className="font-bold text-gray-400">GV:</span>{" "}
            {item.teacher_note || "Không có ghi chú"}
          </p>
        </div>
      </div>

      <a
        href={item.parent_phone ? `tel:${item.parent_phone}` : undefined}
        onClick={(e) => {
          if (!item.parent_phone) e.preventDefault();
        }}
        className="flex w-full items-center justify-center gap-2 min-h-[46px] bg-red-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider active:scale-[0.98] transition-all touch-manipulation"
      >
        <Phone size={15} />
        {item.parent_phone ? `Gọi ${item.parent_phone}` : "Không có SĐT"}
      </a>

      <div>
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">
          Phản hồi CSKH
        </label>
        <input
          type="text"
          placeholder="Ghi lại phản hồi sau khi liên hệ..."
          defaultValue={item.staff_remark || ""}
          onBlur={(e) =>
            handleRemarkBlur(item.attendance_id, e.target.value, item.staff_remark)
          }
          className="w-full min-h-[44px] bg-gray-50 border border-gray-200 focus:border-red-300 focus:ring-2 focus:ring-red-500/10 rounded-xl px-3 py-2.5 text-base outline-none font-medium text-gray-700"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4 text-left">
      <AttendanceHistoryFilter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        isMobile={isMobile}
        onRefresh={() => {
          setPage(1);
          setRefreshKey((k) => k + 1);
        }}
      />

      {!loading && (
        <p className="text-[11px] text-gray-500 font-medium">
          {total === 0 ? "Không có bản ghi phù hợp." : `Tổng ${total} bản ghi`}
        </p>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin w-7 h-7 border-2 border-red-500 border-t-transparent rounded-full" />
        </div>
      )}
      {!loading && filteredData.length === 0 && (
        <p className="text-center text-gray-500 py-8 text-sm font-medium">
          Chưa có bản ghi vắng/đi muộn nào.
        </p>
      )}
      {!loading && isMobile && (
        <div className="space-y-2.5">
          {filteredData.map((item) => (
            <RenderMobileCard key={item.attendance_id} item={item} />
          ))}
        </div>
      )}
      {!loading && !isMobile && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-100">
                  <th className="p-3 min-w-[220px] text-[11px] font-bold text-gray-400 uppercase tracking-wider text-left">
                    Học viên & Phụ huynh
                  </th>
                  <th className="p-3 min-w-[160px] text-[11px] font-bold text-gray-400 uppercase tracking-wider text-left">
                    Lớp học
                  </th>
                  <th className="p-3 min-w-[110px] text-[11px] font-bold text-gray-400 uppercase tracking-wider text-left">
                    Trạng thái
                  </th>
                  <th className="p-3 min-w-[180px] text-[11px] font-bold text-gray-400 uppercase tracking-wider text-left">
                    Ghi chú giáo viên
                  </th>
                  <th className="p-3 min-w-[240px] text-[11px] font-bold text-gray-400 uppercase tracking-wider text-left">
                    Liên hệ & Phản hồi
                  </th>
                  <th className="p-3 w-12" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredData.map((item) => (
                  <tr
                    key={item.attendance_id}
                    className="hover:bg-red-50/30 transition-colors group"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-red-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
                          {(item.student_name || "?").charAt(0)}
                        </div>
                        <div>
                          <div className="font-bold text-gray-800 text-sm group-hover:text-red-600 transition-colors">
                            {item.student_name}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-400 font-medium">
                            <Users size={10} /> {item.parent_name || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-sm font-semibold text-gray-600">{item.class_name}</td>
                    <td className="p-3">{getStatusBadge(item.status)}</td>
                    <td className="p-3 text-[12px] text-gray-500 max-w-[200px]">
                      {item.teacher_note || "—"}
                    </td>
                    <td className="p-3">
                      <div className="space-y-1.5">
                        <button
                          type="button"
                          onClick={() => window.open(`tel:${item.parent_phone}`)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-600 rounded-md text-[10px] font-bold border border-red-100 hover:bg-red-100 transition-all"
                        >
                          <Phone size={11} /> {item.parent_phone || "—"}
                        </button>
                        <input
                          type="text"
                          placeholder="Ghi lại phản hồi CSKH..."
                          defaultValue={item.staff_remark || ""}
                          onBlur={(e) =>
                            handleRemarkBlur(
                              item.attendance_id,
                              e.target.value,
                              item.staff_remark
                            )
                          }
                          className="w-full bg-gray-50 border border-transparent focus:border-red-200 rounded-md px-2.5 py-1.5 text-[11px] outline-none font-medium text-gray-600"
                        />
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <Dropdown
                        menu={{ items: dropdownItems }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <button
                          type="button"
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceHistory;
